import { brandStorage as localStorage } from '@/utils/brandSession';
import React, { useEffect, useState, useCallback } from 'react';
import axios from 'axios';
import {
  Headphones, Mail, Phone, RefreshCw, Settings, Loader2, Plus, Trash2,
  TrendingUp, TrendingDown, AlertTriangle, CheckCircle2, XCircle, Clock, Eye, EyeOff, KeyRound
} from 'lucide-react';
import {
  ComposedChart, Bar, Line, XAxis, YAxis, Tooltip, Legend, ResponsiveContainer, CartesianGrid
} from 'recharts';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../../components/ui/dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import { toast } from 'sonner';
import { useAuth } from '../../App';
import { marketClass } from './cs/marketBenchmarks';
import CsCallsTab from './cs/CsCallsTab';
import CsVoicemailTab from './cs/CsVoicemailTab';
import CsQualityTab from './cs/CsQualityTab';
import CsTargetsTab from './cs/CsTargetsTab';
import CsFeedbackTab from './cs/CsFeedbackTab';
import CsTicketsTab from './cs/CsTicketsTab';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const authHeaders = () => ({ Authorization: `Bearer ${localStorage.getItem('hw_token')}` });

const PRESETS = [
  { id: '7d', label: '7 giorni' },
  { id: '30d', label: '30 giorni' },
  { id: 'month', label: 'Mese corrente' },
  { id: 'quarter', label: 'Trimestre' },
  { id: 'year', label: 'Anno' },
];
const GRANULARITIES = [
  { id: 'day', label: 'Giorno' },
  { id: 'week', label: 'Settimana' },
  { id: 'month', label: 'Mese' },
  { id: 'quarter', label: 'Trimestre' },
  { id: 'year', label: 'Anno' },
];

const iso = (d) => d.toISOString().slice(0, 10);
const presetRange = (preset) => {
  const now = new Date();
  const to = iso(now);
  if (preset === '7d') return { from: iso(new Date(now - 6 * 864e5)), to };
  if (preset === '30d') return { from: iso(new Date(now - 29 * 864e5)), to };
  if (preset === 'month') return { from: iso(new Date(now.getFullYear(), now.getMonth(), 1)), to };
  if (preset === 'quarter') return { from: iso(new Date(now.getFullYear(), Math.floor(now.getMonth() / 3) * 3, 1)), to };
  return { from: iso(new Date(now.getFullYear(), 0, 1)), to };
};

const fmtDur = (secs) => {
  if (secs === null || secs === undefined) return '—';
  const s = Math.round(secs);
  if (s < 60) return `${s}s`;
  if (s < 3600) return `${Math.round(s / 60)}m`;
  if (s < 86400) return `${Math.floor(s / 3600)}h ${Math.round((s % 3600) / 60)}m`;
  return `${Math.floor(s / 86400)}g ${Math.round((s % 86400) / 3600)}h`;
};

const fmtBucket = (isoStr, granularity) => {
  const d = new Date(isoStr);
  if (granularity === 'year') return `${d.getFullYear()}`;
  if (granularity === 'quarter') return `T${Math.floor(d.getMonth() / 3) + 1} ${d.getFullYear()}`;
  if (granularity === 'month') return d.toLocaleDateString('it-IT', { month: 'short', year: '2-digit' });
  if (granularity === 'week') return `sett. ${d.toLocaleDateString('it-IT', { day: '2-digit', month: '2-digit' })}`;
  return d.toLocaleDateString('it-IT', { day: '2-digit', month: '2-digit' });
};

const Delta = ({ current, previous, invert = false, isDuration = false, testId }) => {
  if (current === null || current === undefined || !previous) return null;
  const diff = current - previous;
  if (!previous || Math.abs(diff) < 1e-9) return null;
  const pct = Math.round((diff / previous) * 100);
  const good = invert ? diff < 0 : diff > 0;
  return (
    <span data-testid={testId} className={`inline-flex items-center gap-0.5 text-xs font-medium ${good ? 'text-emerald-600' : 'text-rose-600'}`}>
      {diff > 0 ? <TrendingUp className="h-3 w-3" /> : <TrendingDown className="h-3 w-3" />}
      {pct > 0 ? '+' : ''}{pct}%
    </span>
  );
};

const KpiCard = ({ title, value, sub, delta, testId, valueClass }) => (
  <div data-testid={testId} className="bg-white rounded-xl border border-slate-200 p-4 shadow-sm">
    <p className="text-xs text-slate-500 uppercase tracking-wide">{title}</p>
    <div className="flex items-baseline gap-2 mt-1">
      <span className={`text-2xl font-bold ${valueClass || 'text-slate-800'}`}>{value}</span>
      {delta}
    </div>
    {sub && <p className="text-xs text-slate-400 mt-0.5">{sub}</p>}
  </div>
);

export default function CustomerServiceControl() {
  const [preset, setPreset] = useState('30d');
  const [range, setRange] = useState(presetRange('30d'));
  const [granularity, setGranularity] = useState('day');
  const [summary, setSummary] = useState(null);
  const [buckets, setBuckets] = useState([]);
  const [operators, setOperators] = useState([]);
  const [unanswered, setUnanswered] = useState([]);
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [syncing, setSyncing] = useState(false);
  const [showSettings, setShowSettings] = useState(false);
  const [activeTab, setActiveTab] = useState('overview');
  const { user } = useAuth();
  const isAdmin = user?.role === 'admin';

  const loadAll = useCallback(async () => {
    setLoading(true);
    try {
      const params = { date_from: range.from, date_to: range.to };
      const h = { headers: authHeaders() };
      const [sRes, tRes, oRes, uRes, stRes] = await Promise.all([
        axios.get(`${API}/cs-control/metrics/summary`, { params, ...h }),
        axios.get(`${API}/cs-control/metrics/timeseries`, { params: { ...params, granularity }, ...h }),
        axios.get(`${API}/cs-control/metrics/operators`, { params, ...h }),
        axios.get(`${API}/cs-control/emails/unanswered`, { params: { hours: 24 }, ...h }),
        axios.get(`${API}/cs-control/status`, h),
      ]);
      setSummary(sRes.data);
      setBuckets(tRes.data.buckets || []);
      setOperators(oRes.data.operators || []);
      setUnanswered(uRes.data.emails || []);
      setStatus(stRes.data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Errore caricamento metriche');
    } finally {
      setLoading(false);
    }
  }, [range, granularity]);

  useEffect(() => { loadAll(); }, [loadAll]);

  const handlePreset = (p) => { setPreset(p); setRange(presetRange(p)); };

  const handleSync = async () => {
    setSyncing(true);
    try {
      const res = await axios.post(`${API}/cs-control/sync`, {}, { headers: authHeaders() });
      if (res.data.background) {
        toast.info(res.data.already_running ? 'Sincronizzazione già in corso...' : 'Sincronizzazione avviata in background — i dati si aggiornano tra poco');
        setTimeout(() => { loadAll(); }, 12000);
        setTimeout(() => { loadAll(); setSyncing(false); }, 35000);
        return;
      }
      toast.success('Sync completato');
      loadAll();
      setSyncing(false);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Errore sincronizzazione');
      setSyncing(false);
    }
  };

  const notConfigured = status && !status.voxloud_last_sync && (status.mailboxes || []).length === 0
    && status.emails_stored === 0 && status.calls_stored === 0;

  const em = summary?.email || {};
  const pe = summary?.previous?.email || {};
  const ca = summary?.calls || {};
  const pc = summary?.previous?.calls || {};

  const chartData = buckets.map(b => ({
    name: fmtBucket(b.bucket, granularity),
    'Email ricevute': b.emails_in,
    'Email inviate': b.emails_out,
    'Risposta (min)': b.response_avg_seconds != null ? Math.round(b.response_avg_seconds / 60) : null,
    'Risposte': b.calls_answered,
    'Perse': b.calls_missed,
    'Attesa (s)': b.wait_avg_seconds != null ? Math.round(b.wait_avg_seconds) : null,
  }));

  return (
    <div data-testid="cs-control-page" className="p-6 space-y-6 max-w-[1500px]">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex items-center gap-3">
          <div className="bg-indigo-100 text-indigo-700 rounded-xl p-2.5"><Headphones className="h-6 w-6" /></div>
          <div>
            <h1 className="text-2xl font-bold text-slate-800">Performance Customer Service</h1>
            <p className="text-sm text-slate-500">Metriche email e telefonate — ufficio e singoli operatori</p>
          </div>
        </div>
        {isAdmin && (
        <div className="flex items-center gap-2">
          <Button data-testid="cs-sync-btn" variant="outline" onClick={handleSync} disabled={syncing}>
            {syncing ? <Loader2 className="h-4 w-4 mr-1 animate-spin" /> : <RefreshCw className="h-4 w-4 mr-1" />}
            Sincronizza ora
          </Button>
          <Button data-testid="cs-settings-btn" onClick={() => setShowSettings(true)}>
            <Settings className="h-4 w-4 mr-1" /> Impostazioni
          </Button>
        </div>
        )}
      </div>

      {notConfigured && (
        <div data-testid="cs-not-configured-banner" className="bg-amber-50 border border-amber-300 rounded-xl p-4 flex items-start gap-3">
          <AlertTriangle className="h-5 w-5 text-amber-600 mt-0.5" />
          <div className="text-sm text-amber-800">
            <p className="font-semibold">Sistema pronto ma non ancora collegato.</p>
            <p>Per attivare le metriche apri <b>Impostazioni</b> e inserisci: la <b>chiave API Voxloud</b> (pannello Voxloud → Integrazioni → API key) e le <b>caselle email IONOS</b> (password IMAP; se la casella ha la 2FA serve una app-password). Poi mappa gli <b>operatori</b> (nome + email + interno).</p>
          </div>
        </div>
      )}

      {/* Tab principali */}
      <div className="flex gap-1 border-b border-slate-200 overflow-x-auto" data-testid="cs-tabs">
        {[['overview', '📊 Panoramica'], ['calls', '☎️ Chiamate'], ['voicemail', '📞 Da richiamare'], ['tickets', '🎫 Ticket'], ['feedback', '⭐ Feedback'], ['quality', '✨ Qualità AI'], ['targets', '🎯 Obiettivi']].map(([id, label]) => (
          <button key={id} data-testid={`cs-tab-${id}`} onClick={() => setActiveTab(id)}
            className={`px-4 py-2 text-sm font-medium border-b-2 -mb-px whitespace-nowrap transition-colors ${activeTab === id ? 'border-indigo-600 text-indigo-700' : 'border-transparent text-slate-500 hover:text-slate-700'}`}>
            {label}
          </button>
        ))}
      </div>

      {activeTab === 'overview' && (<>
      {/* Filtri periodo */}
      <div className="flex flex-wrap items-center gap-2">
        {PRESETS.map(p => (
          <button key={p.id} data-testid={`cs-preset-${p.id}`} onClick={() => handlePreset(p.id)}
            className={`px-3 py-1.5 rounded-full text-sm border transition-colors ${preset === p.id ? 'bg-indigo-600 text-white border-indigo-600' : 'bg-white text-slate-600 border-slate-300 hover:border-indigo-400'}`}>
            {p.label}
          </button>
        ))}
        <div className="flex items-center gap-1 ml-2">
          <Input data-testid="cs-date-from" type="date" value={range.from} className="w-36 h-8"
            onChange={e => { setPreset(null); setRange(r => ({ ...r, from: e.target.value })); }} />
          <span className="text-slate-400">→</span>
          <Input data-testid="cs-date-to" type="date" value={range.to} className="w-36 h-8"
            onChange={e => { setPreset(null); setRange(r => ({ ...r, to: e.target.value })); }} />
        </div>
        <div className="flex items-center gap-1 ml-auto">
          <span className="text-xs text-slate-500">Raggruppa per:</span>
          {GRANULARITIES.map(g => (
            <button key={g.id} data-testid={`cs-gran-${g.id}`} onClick={() => setGranularity(g.id)}
              className={`px-2.5 py-1 rounded-md text-xs border ${granularity === g.id ? 'bg-slate-800 text-white border-slate-800' : 'bg-white text-slate-600 border-slate-300'}`}>
              {g.label}
            </button>
          ))}
        </div>
      </div>

      {loading ? (
        <div className="flex justify-center py-16"><Loader2 className="h-8 w-8 animate-spin text-indigo-500" /></div>
      ) : (
        <>
          {/* KPI Email */}
          <div>
            <h2 className="text-sm font-semibold text-slate-600 flex items-center gap-1.5 mb-2"><Mail className="h-4 w-4" /> EMAIL</h2>
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3">
              <KpiCard testId="cs-kpi-emails-in" title="Ricevute" value={em.inbound ?? 0}
                delta={<Delta current={em.inbound} previous={pe.inbound} />} />
              <KpiCard testId="cs-kpi-emails-out" title="Inviate" value={em.outbound ?? 0}
                delta={<Delta current={em.outbound} previous={pe.outbound} />} />
              <KpiCard testId="cs-kpi-response-avg" title="Prima risposta (media)" value={fmtDur(em.response_avg_seconds)}
                delta={<Delta current={em.response_avg_seconds} previous={pe.response_avg_seconds} invert isDuration />} />
              <KpiCard testId="cs-kpi-response-median" title="Prima risposta (mediana)" value={fmtDur(em.response_median_seconds)}
                sub="mercato 5,4h"
                valueClass={marketClass(em.response_median_seconds != null ? em.response_median_seconds / 3600 : null, 'email_median_first_reply_hours')} />
              <KpiCard testId="cs-kpi-sla" title={`Entro SLA (${em.sla_hours || 24}h)`}
                value={em.within_sla_pct != null ? `${em.within_sla_pct}%` : '—'}
                sub="mercato 90%"
                valueClass={marketClass(em.within_sla_pct, 'email_within_24h_pct')}
                delta={<Delta current={em.within_sla_pct} previous={pe.within_sla_pct} />} />
              <KpiCard testId="cs-kpi-unanswered" title="Senza risposta >24h" value={unanswered.length}
                sub="in attesa adesso" />
            </div>
          </div>

          {/* KPI Telefonate */}
          <div>
            <h2 className="text-sm font-semibold text-slate-600 flex items-center gap-1.5 mb-2"><Phone className="h-4 w-4" /> TELEFONATE</h2>
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3">
              <KpiCard testId="cs-kpi-calls-in" title="In entrata" value={ca.inbound ?? 0}
                delta={<Delta current={ca.inbound} previous={pc.inbound} />} />
              <KpiCard testId="cs-kpi-calls-answered" title="Risposte" value={ca.answered ?? 0}
                delta={<Delta current={ca.answered} previous={pc.answered} />} />
              <KpiCard testId="cs-kpi-calls-missed" title="Perse in orario" value={ca.missed_in_hours ?? ca.missed ?? 0}
                delta={<Delta current={ca.missed_in_hours} previous={pc.missed_in_hours} invert />} />
              <KpiCard testId="cs-kpi-out-of-hours" title="Fuori orario" value={ca.out_of_hours ?? 0}
                sub="ufficio chiuso" delta={<Delta current={ca.out_of_hours} previous={pc.out_of_hours} invert />} />
              <KpiCard testId="cs-kpi-abandoned" title="Abbandonate" value={ca.abandoned ?? 0}
                sub="riagganciate <5s" delta={<Delta current={ca.abandoned} previous={pc.abandoned} invert />} />
              <KpiCard testId="cs-kpi-answer-rate" title="Tasso risposta"
                value={ca.answer_rate_pct != null ? `${ca.answer_rate_pct}%` : '—'} sub="in orario"
                delta={<Delta current={ca.answer_rate_pct} previous={pc.answer_rate_pct} />} />
              <KpiCard testId="cs-kpi-wait-avg" title="Attesa media" value={fmtDur(ca.wait_avg_seconds)}
                delta={<Delta current={ca.wait_avg_seconds} previous={pc.wait_avg_seconds} invert />} />
              <KpiCard testId="cs-kpi-talk-avg" title="Durata media" value={fmtDur(ca.talk_avg_seconds)} sub="conversazione" />
              <KpiCard testId="cs-kpi-voicemails" title="Voicemail" value={ca.voicemails ?? 0} sub="messaggi lasciati" />
              <KpiCard testId="cs-kpi-language" title="Italiano / Inglese"
                value={`${ca.by_language?.italiano ?? 0} / ${ca.by_language?.inglese ?? 0}`} sub="per linea" />
            </div>
          </div>

          {/* Grafici */}
          <div className="grid lg:grid-cols-2 gap-4">
            <div className="bg-white rounded-xl border border-slate-200 p-4" data-testid="cs-chart-emails">
              <h3 className="text-sm font-semibold text-slate-700 mb-2">📧 Email: volumi e tempo di prima risposta</h3>
              <ResponsiveContainer width="100%" height={260}>
                <ComposedChart data={chartData}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                  <XAxis dataKey="name" tick={{ fontSize: 11 }} />
                  <YAxis yAxisId="l" tick={{ fontSize: 11 }} />
                  <YAxis yAxisId="r" orientation="right" tick={{ fontSize: 11 }} />
                  <Tooltip />
                  <Legend wrapperStyle={{ fontSize: 12 }} />
                  <Bar yAxisId="l" dataKey="Email ricevute" fill="#6366f1" radius={[3, 3, 0, 0]} />
                  <Bar yAxisId="l" dataKey="Email inviate" fill="#a5b4fc" radius={[3, 3, 0, 0]} />
                  <Line yAxisId="r" type="monotone" dataKey="Risposta (min)" stroke="#f59e0b" strokeWidth={2} dot={false} connectNulls />
                </ComposedChart>
              </ResponsiveContainer>
            </div>
            <div className="bg-white rounded-xl border border-slate-200 p-4" data-testid="cs-chart-calls">
              <h3 className="text-sm font-semibold text-slate-700 mb-2">☎️ Telefonate: risposte/perse e attesa</h3>
              <ResponsiveContainer width="100%" height={260}>
                <ComposedChart data={chartData}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                  <XAxis dataKey="name" tick={{ fontSize: 11 }} />
                  <YAxis yAxisId="l" tick={{ fontSize: 11 }} />
                  <YAxis yAxisId="r" orientation="right" tick={{ fontSize: 11 }} />
                  <Tooltip />
                  <Legend wrapperStyle={{ fontSize: 12 }} />
                  <Bar yAxisId="l" dataKey="Risposte" fill="#10b981" radius={[3, 3, 0, 0]} />
                  <Bar yAxisId="l" dataKey="Perse" fill="#f43f5e" radius={[3, 3, 0, 0]} />
                  <Line yAxisId="r" type="monotone" dataKey="Attesa (s)" stroke="#0ea5e9" strokeWidth={2} dot={false} connectNulls />
                </ComposedChart>
              </ResponsiveContainer>
            </div>
          </div>

          {/* Tabella operatori */}
          <div className="bg-white rounded-xl border border-slate-200 overflow-hidden" data-testid="cs-operators-table">
            <div className="px-4 py-3 border-b border-slate-100">
              <h3 className="text-sm font-semibold text-slate-700">👤 Dettaglio per operatore</h3>
            </div>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-slate-50 text-slate-500 text-xs uppercase">
                  <tr>
                    <th className="text-left px-4 py-2">Operatore</th>
                    <th className="text-right px-3 py-2">Email inviate</th>
                    <th className="text-right px-3 py-2">Email gestite</th>
                    <th className="text-right px-3 py-2">Risposta media</th>
                    <th className="text-right px-3 py-2">Mediana</th>
                    <th className="text-right px-3 py-2">Entro SLA</th>
                    <th className="text-right px-3 py-2">Chiamate risposte</th>
                    <th className="text-right px-3 py-2">Perse</th>
                    <th className="text-right px-3 py-2">Attesa media</th>
                    <th className="text-right px-4 py-2">Durata media</th>
                  </tr>
                </thead>
                <tbody>
                  {operators.length === 0 && (
                    <tr><td colSpan={10} className="px-4 py-6 text-center text-slate-400">
                      Nessun operatore mappato — aggiungili dalle Impostazioni
                    </td></tr>
                  )}
                  {operators.map((op, i) => (
                    <tr key={op.operator_id || `un-${i}`} className="border-t border-slate-100 hover:bg-slate-50">
                      <td className="px-4 py-2 font-medium text-slate-700">{op.name}</td>
                      <td className="text-right px-3 py-2">{op.emails_sent}</td>
                      <td className="text-right px-3 py-2">{op.emails_responded}</td>
                      <td className="text-right px-3 py-2">{fmtDur(op.response_avg_seconds)}</td>
                      <td className={`text-right px-3 py-2 font-semibold ${marketClass(op.response_median_seconds != null ? op.response_median_seconds / 3600 : null, 'email_median_first_reply_hours')}`}>{fmtDur(op.response_median_seconds)}</td>
                      <td className={`text-right px-3 py-2 font-semibold ${marketClass(op.within_sla_pct, 'email_within_24h_pct')}`}>{op.within_sla_pct != null ? `${op.within_sla_pct}%` : '—'}</td>
                      <td className="text-right px-3 py-2">{op.calls_answered}</td>
                      <td className="text-right px-3 py-2">{op.calls_missed}</td>
                      <td className={`text-right px-3 py-2 font-semibold ${marketClass(op.wait_avg_seconds, 'wait_avg_seconds')}`}>{fmtDur(op.wait_avg_seconds)}</td>
                      <td className="text-right px-4 py-2">{fmtDur(op.talk_avg_seconds)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          {/* Email senza risposta */}
          <div className="bg-white rounded-xl border border-slate-200 overflow-hidden" data-testid="cs-unanswered-table">
            <div className="px-4 py-3 border-b border-slate-100 flex items-center gap-2">
              <Clock className="h-4 w-4 text-rose-500" />
              <h3 className="text-sm font-semibold text-slate-700">Email in attesa di risposta da più di 24h</h3>
            </div>
            {unanswered.length === 0 ? (
              <p className="px-4 py-5 text-sm text-emerald-600 flex items-center gap-1.5">
                <CheckCircle2 className="h-4 w-4" /> Nessuna email in attesa oltre soglia
              </p>
            ) : (
              <div className="overflow-x-auto max-h-72 overflow-y-auto">
                <table className="w-full text-sm">
                  <thead className="bg-slate-50 text-slate-500 text-xs uppercase sticky top-0">
                    <tr>
                      <th className="text-left px-4 py-2">Mittente</th>
                      <th className="text-left px-3 py-2">Oggetto</th>
                      <th className="text-left px-3 py-2">Casella</th>
                      <th className="text-right px-4 py-2">In attesa da</th>
                    </tr>
                  </thead>
                  <tbody>
                    {unanswered.map((m, i) => (
                      <tr key={i} className="border-t border-slate-100">
                        <td className="px-4 py-2">{m.from_addr}</td>
                        <td className="px-3 py-2 text-slate-600 max-w-md truncate">{m.subject}</td>
                        <td className="px-3 py-2 text-slate-400 text-xs">{m.mailbox_email}</td>
                        <td className="text-right px-4 py-2 font-medium text-rose-600">{Math.round(m.hours_waiting)}h</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </>
      )}
      </>)}

      {activeTab === 'calls' && <CsCallsTab />}
      {activeTab === 'voicemail' && <CsVoicemailTab />}
      {activeTab === 'tickets' && <CsTicketsTab />}
      {activeTab === 'feedback' && <CsFeedbackTab isAdmin={isAdmin} />}
      {activeTab === 'quality' && <CsQualityTab />}
      {activeTab === 'targets' && <CsTargetsTab />}

      <CsSettingsDialog open={showSettings} onClose={() => { setShowSettings(false); loadAll(); }} />
    </div>
  );
}

// ============================ IMPOSTAZIONI ============================

function CsSettingsDialog({ open, onClose }) {
  const [settings, setSettings] = useState(null);
  const [ops, setOps] = useState([]);
  const [voxKey, setVoxKey] = useState('');
  const [testing, setTesting] = useState(false);
  const [newOp, setNewOp] = useState({ name: '', emails: '', extensions: '', voxloud_names: '' });
  const [suggestions, setSuggestions] = useState([]);
  const [newBox, setNewBox] = useState({ label: '', email: '', password: '', host: 'imap.ionos.it', port: 993 });
  const [showNewPw, setShowNewPw] = useState(false);
  const [editBox, setEditBox] = useState(null); // { id, label, email, host, port, password, show }
  const [exclusions, setExclusions] = useState('');
  const [te, setTe] = useState(null);
  const [teLog, setTeLog] = useState([]);
  const [teStats, setTeStats] = useState(null);
  const [waTestPhone, setWaTestPhone] = useState('');
  const [waTestBusy, setWaTestBusy] = useState(false);
  const [waTestLang, setWaTestLang] = useState('it');
  const [teBusy, setTeBusy] = useState(false);
  const [saving, setSaving] = useState(false);
  const [popupBusy, setPopupBusy] = useState(false);
  const [pbBusy, setPbBusy] = useState(false);
  const [pbStatus, setPbStatus] = useState(null);
  const [dup, setDup] = useState(null);
  const [dupBusy, setDupBusy] = useState(false);
  const [dupOnlySusp, setDupOnlySusp] = useState(true);
  const [ph, setPh] = useState({ weekdays: '09:00-13:00, 14:00-18:00', saturday: '', sunday: '' });

  const loadDuplicates = async () => {
    setDupBusy(true);
    try {
      const r = await axios.get(`${API}/cs-control/directory/duplicates`, { headers: authHeaders() });
      setDup(r.data);
      toast.success(`Analisi completata: ${r.data.total} numeri su più schede, ${r.data.suspicious} con nomi diversi`);
    } catch (e) {
      toast.error('Errore analisi rubrica');
    } finally {
      setDupBusy(false);
    }
  };

  const loadPbStatus = async () => {
    try {
      const r = await axios.get(`${API}/cs-control/phonebook/status`, { headers: authHeaders() });
      setPbStatus(r.data);
    } catch (_) { /* silenzioso */ }
  };

  const pbDry = async () => {
    setPbBusy(true);
    try {
      const r = await axios.post(`${API}/cs-control/phonebook/sync`, { dry_run: true }, { headers: authHeaders() });
      if (r.data.ok) {
        toast.success(`Anteprima: ${r.data.create} da creare, ${r.data.update} da aggiornare, ${r.data.delete} da eliminare (rubrica PMS: ${r.data.desired} numeri)`);
        setPbStatus(p => ({ ...(p || {}), last_result: r.data }));
      } else toast.error(r.data.error || 'Errore anteprima');
    } catch (e) { toast.error(e?.response?.data?.detail || 'Errore anteprima sync'); }
    setPbBusy(false);
  };

  const pbRun = async () => {
    if (!window.confirm('Sincronizzare ORA la rubrica sul centralino Voxloud? Verranno creati/aggiornati i contatti marcati HWsmart (quelli manuali non vengono toccati).')) return;
    setPbBusy(true);
    try {
      await axios.post(`${API}/cs-control/phonebook/sync`, {}, { headers: authHeaders() });
      toast.success('Sincronizzazione avviata in background — premi "Aggiorna stato" tra qualche minuto');
      setTimeout(loadPbStatus, 5000);
    } catch (e) { toast.error(e?.response?.data?.detail || 'Errore avvio sync'); }
    setPbBusy(false);
  };

  const pbToggle = async (enabled) => {
    try {
      await axios.post(`${API}/cs-control/phonebook/config`, { enabled }, { headers: authHeaders() });
      setPbStatus(p => ({ ...(p || {}), enabled }));
      toast.success(enabled ? 'Sync notturno rubrica ATTIVATO (produzione, ore 4:40)' : 'Sync notturno disattivato');
    } catch (e) { toast.error(e?.response?.data?.detail || 'Errore'); }
  };

  useEffect(() => { loadPbStatus(); }, []);

  const saveExclusions = async () => {
    setSaving(true);
    try {
      const list = exclusions.split('\n').map(s => s.trim()).filter(Boolean);
      await axios.put(`${API}/cs-control/settings/email-exclusions`, { exclusions: list }, { headers: authHeaders() });
      toast.success('Esclusioni salvate — le metriche si aggiornano subito, anche sul passato');
    } catch (e) { toast.error(e?.response?.data?.detail || 'Errore salvataggio esclusioni'); }
    finally { setSaving(false); }
  };

  const loadTeLog = async () => {
    try {
      const r = await axios.get(`${API}/cs-control/transcript-email/log`, { headers: authHeaders() });
      setTeLog(r.data.rows || []);
    } catch (_) { /* opzionale */ }
    try {
      const s = await axios.get(`${API}/cs-control/transcript-email/stats`, { headers: authHeaders() });
      setTeStats(s.data);
    } catch (_) { /* opzionale */ }
  };

  const saveTe = async () => {
    setSaving(true);
    try {
      const r = await axios.put(`${API}/cs-control/settings/transcript-email`, te, { headers: authHeaders() });
      setTe(r.data.transcript_email);
      toast.success('Impostazioni trascrizioni salvate ✅');
    } catch (e) { toast.error(e.response?.data?.detail || 'Errore salvataggio'); }
    finally { setSaving(false); }
  };

  const savePh = async () => {
    setSaving(true);
    try {
      const r = await axios.put(`${API}/cs-control/settings/popup-hours`, ph, { headers: authHeaders() });
      setPh(r.data.call_popup_hours);
      toast.success('Orari popup salvati ✅');
    } catch (e) { toast.error(e.response?.data?.detail || 'Errore salvataggio orari'); }
    finally { setSaving(false); }
  };

  const runTeNow = async () => {
    setTeBusy(true);
    try {
      await axios.post(`${API}/cs-control/transcript-email/run`, {}, { headers: authHeaders() });
      toast.success('Controllo avviato in background — il log si aggiorna tra 1-2 minuti');
      setTimeout(loadTeLog, 45000);
    } catch (e) { toast.error(e.response?.data?.detail || 'Errore'); }
    finally { setTeBusy(false); }
  };

  const load = useCallback(async () => {
    try {
      const h = { headers: authHeaders() };
      const [sRes, oRes] = await Promise.all([
        axios.get(`${API}/cs-control/settings`, h),
        axios.get(`${API}/cs-control/operators`, h),
      ]);
      setSettings(sRes.data);
      setExclusions((sRes.data.email_exclusions || []).join('\n'));
      setPh(sRes.data.call_popup_hours || { weekdays: '09:00-13:00, 14:00-18:00', saturday: '', sunday: '' });
      setTe(sRes.data.transcript_email || { mode: 'test', test_recipient: 'mas.marchese@holidayw.eu', fallback_to_test_recipient: true });
      loadTeLog();
      setOps(oRes.data.operators || []);
      try {
        const aRes = await axios.get(`${API}/cs-control/voxloud/answerers`, h);
        setSuggestions(aRes.data.answerers || []);
      } catch (_) { /* opzionale */ }
    } catch (e) {
      toast.error('Errore caricamento impostazioni');
    }
  }, []);

  useEffect(() => { if (open) { load(); setVoxKey(''); } }, [open, load]);

  const saveVoxloud = async () => {
    if (!voxKey.trim()) { toast.error('Inserisci la chiave API'); return; }
    setSaving(true);
    try {
      await axios.put(`${API}/cs-control/settings/voxloud`, { api_key: voxKey.trim() }, { headers: authHeaders() });
      toast.success('Chiave Voxloud salvata');
      setVoxKey('');
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Errore salvataggio'); }
    finally { setSaving(false); }
  };

  const testVoxloud = async () => {
    setTesting(true);
    try {
      const res = await axios.post(`${API}/cs-control/settings/voxloud/test`,
        voxKey.trim() ? { api_key: voxKey.trim() } : {}, { headers: authHeaders() });
      if (res.data.ok) toast.success('Connessione Voxloud OK ✅');
      else toast.error(res.data.error || 'Test fallito');
    } catch (e) { toast.error(e?.response?.data?.detail || 'Test fallito'); }
    finally { setTesting(false); }
  };

  const setupPopup = async () => {
    setPopupBusy(true);
    try {
      const res = await axios.post(`${API}/cs-control/voxloud/webhook-setup`, {}, { headers: authHeaders() });
      if (res.data.ok) toast.success('Popup chiamate ATTIVATO ✅ — webhook Voxloud registrati');
      else toast.error(`Attivazione parziale: ${(res.data.events || []).map(e => `${e.event}: ${e.status}`).join(', ')}`);
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Errore attivazione popup'); }
    finally { setPopupBusy(false); }
  };

  const removePopup = async () => {
    setPopupBusy(true);
    try {
      await axios.delete(`${API}/cs-control/voxloud/webhook-setup`, { headers: authHeaders() });
      toast.success('Popup chiamate disattivato');
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Errore disattivazione'); }
    finally { setPopupBusy(false); }
  };

  const addOperator = async () => {
    if (!newOp.name.trim()) { toast.error('Nome obbligatorio'); return; }
    try {
      await axios.post(`${API}/cs-control/operators`, {
        name: newOp.name,
        emails: newOp.emails.split(',').map(s => s.trim()).filter(Boolean),
        extensions: newOp.extensions.split(',').map(s => s.trim()).filter(Boolean),
        voxloud_names: newOp.voxloud_names.split(',').map(s => s.trim()).filter(Boolean),
      }, { headers: authHeaders() });
      toast.success('Operatore aggiunto');
      setNewOp({ name: '', emails: '', extensions: '', voxloud_names: '' });
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Errore'); }
  };

  const deleteOperator = async (id) => {
    await axios.delete(`${API}/cs-control/operators/${id}`, { headers: authHeaders() });
    load();
  };

  const addMailbox = async () => {
    if (!newBox.email.trim() || !newBox.password) { toast.error('Email e password obbligatorie'); return; }
    setSaving(true);
    try {
      await axios.post(`${API}/cs-control/settings/mailboxes`, newBox, { headers: authHeaders() });
      toast.success('Casella aggiunta');
      setNewBox({ label: '', email: '', password: '', host: 'imap.ionos.it', port: 993 });
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Errore'); }
    finally { setSaving(false); }
  };

  const testMailbox = async (id) => {
    try {
      const res = await axios.post(`${API}/cs-control/settings/mailboxes/${id}/test`, {}, { headers: authHeaders() });
      if (res.data.ok) toast.success(`Connessione OK ✅ — cartella inviata: ${res.data.sent_folder}, ${res.data.inbox_messages} msg in INBOX`);
      else toast.error(res.data.error || 'Test fallito');
    } catch (e) { toast.error('Test fallito'); }
  };

  const deleteMailbox = async (id) => {
    await axios.delete(`${API}/cs-control/settings/mailboxes/${id}`, { headers: authHeaders() });
    load();
  };

  const saveMailboxPassword = async () => {
    if (!editBox?.email?.trim()) { toast.error('Email obbligatoria'); return; }
    setSaving(true);
    try {
      const payload = { label: editBox.label, email: editBox.email, host: editBox.host, port: editBox.port };
      if (editBox.password) payload.password = editBox.password;
      await axios.put(`${API}/cs-control/settings/mailboxes/${editBox.id}`, payload, { headers: authHeaders() });
      toast.success('Casella aggiornata — premi Test per verificare');
      setEditBox(null);
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Errore aggiornamento casella'); }
    finally { setSaving(false); }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent data-testid="cs-settings-dialog" className="max-w-3xl max-h-[85vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Impostazioni Performance Customer Service</DialogTitle>
        </DialogHeader>
        <Tabs defaultValue="operators">
          <TabsList className="grid grid-cols-5 w-full">
            <TabsTrigger value="operators" data-testid="cs-tab-operators">👤 Operatori</TabsTrigger>
            <TabsTrigger value="mailboxes" data-testid="cs-tab-mailboxes">📧 Caselle Email</TabsTrigger>
            <TabsTrigger value="voxloud" data-testid="cs-tab-voxloud">☎️ Voxloud</TabsTrigger>
            <TabsTrigger value="transcripts" data-testid="cs-tab-transcripts">📝 Trascrizioni</TabsTrigger>
            <TabsTrigger value="rubrica" data-testid="cs-tab-rubrica">🧹 Rubrica</TabsTrigger>
          </TabsList>

          <TabsContent value="operators" className="space-y-3 pt-3">
            <p className="text-xs text-slate-500">Mappa ogni persona con le sue email (per attribuire le risposte) e il suo interno telefonico (per attribuire le chiamate). Le mappature valgono anche retroattivamente.</p>
            {ops.map(op => (
              <div key={op.id} className="flex items-center justify-between border border-slate-200 rounded-lg px-3 py-2">
                <div>
                  <p className="font-medium text-sm">{op.name}</p>
                  <p className="text-xs text-slate-500">
                    {op.emails?.length ? `📧 ${op.emails.join(', ')}` : '📧 nessuna email'} · {op.voxloud_names?.length ? `☎️ ${op.voxloud_names.join(', ')}` : (op.extensions?.length ? `☎️ int. ${op.extensions.join(', ')}` : '☎️ non collegato al centralino')}
                  </p>
                </div>
                <Button variant="ghost" size="sm" data-testid={`cs-del-operator-${op.id}`} onClick={() => deleteOperator(op.id)}>
                  <Trash2 className="h-4 w-4 text-rose-500" />
                </Button>
              </div>
            ))}
            <div className="border border-dashed border-slate-300 rounded-lg p-3 space-y-2">
              {suggestions.length > 0 && (
                <div className="space-y-1">
                  <p className="text-xs text-slate-500">Rilevati dal centralino (clicca per usare):</p>
                  <div className="flex flex-wrap gap-1.5" data-testid="cs-voxloud-suggestions">
                    {suggestions.filter(s => !ops.some(o => (o.voxloud_names || []).includes(s.name))).map(s => (
                      <button key={s.name} type="button"
                        onClick={() => setNewOp({ ...newOp, name: newOp.name || s.name.split(' ')[0], voxloud_names: s.name })}
                        className="text-xs px-2 py-1 rounded-full bg-indigo-50 text-indigo-700 border border-indigo-200 hover:bg-indigo-100"
                        data-testid={`cs-suggestion-${s.name.replace(/\s+/g, '-')}`}>
                        ☎️ {s.name} ({s.calls} risposte)
                      </button>
                    ))}
                  </div>
                </div>
              )}
              <div className="grid grid-cols-2 gap-2">
                <div><Label className="text-xs">Nome</Label>
                  <Input data-testid="cs-new-op-name" value={newOp.name} onChange={e => setNewOp({ ...newOp, name: e.target.value })} placeholder="Mario Rossi" /></div>
                <div><Label className="text-xs">Nome su Voxloud (virgola-separati)</Label>
                  <Input data-testid="cs-new-op-voxnames" value={newOp.voxloud_names} onChange={e => setNewOp({ ...newOp, voxloud_names: e.target.value })} placeholder="Elisa Servizio italiano" /></div>
                <div><Label className="text-xs">Email (virgola-separate)</Label>
                  <Input data-testid="cs-new-op-emails" value={newOp.emails} onChange={e => setNewOp({ ...newOp, emails: e.target.value })} placeholder="mario@holidayw.eu" /></div>
                <div><Label className="text-xs">Interni/numeri (virgola-separati)</Label>
                  <Input data-testid="cs-new-op-extensions" value={newOp.extensions} onChange={e => setNewOp({ ...newOp, extensions: e.target.value })} placeholder="101 oppure 3935159..." /></div>
              </div>
              <Button size="sm" data-testid="cs-add-operator-btn" onClick={addOperator}><Plus className="h-4 w-4 mr-1" /> Aggiungi operatore</Button>
            </div>
          </TabsContent>

          <TabsContent value="mailboxes" className="space-y-3 pt-3">
            <p className="text-xs text-slate-500">Caselle IONOS lette in SOLA LETTURA (solo intestazioni, mai il contenuto). Se la casella ha la verifica in 2 passaggi, usa una <b>app-password</b> (Webmail IONOS → Login e Sicurezza → Password app).</p>
            {(settings?.mailboxes || []).map(mb => (
              <div key={mb.id} className="border border-slate-200 rounded-lg px-3 py-2 space-y-2">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="font-medium text-sm">{mb.label} <span className="text-slate-400 font-normal">({mb.email})</span></p>
                    <p className="text-xs text-slate-500">
                      {mb.host && mb.host !== 'imap.ionos.it' && <span className="text-amber-600 font-medium">host: {mb.host} · </span>}
                      {mb.last_sync ? `Ultimo sync: ${new Date(mb.last_sync).toLocaleString('it-IT')}` : 'Mai sincronizzata'}
                      {mb.last_error && <span className="text-rose-500"> · ⚠️ {mb.last_error}</span>}
                    </p>
                  </div>
                  <div className="flex gap-1">
                    <Button variant="outline" size="sm" data-testid={`cs-edit-pw-mailbox-${mb.id}`} title="Modifica casella"
                      onClick={() => setEditBox(editBox?.id === mb.id ? null : {
                        id: mb.id, label: mb.label || '', email: mb.email || '',
                        host: mb.host || 'imap.ionos.it', port: mb.port || 993, password: '', show: false })}>
                      <KeyRound className="h-4 w-4" />
                    </Button>
                    <Button variant="outline" size="sm" data-testid={`cs-test-mailbox-${mb.id}`} onClick={() => testMailbox(mb.id)}>Test</Button>
                    <Button variant="ghost" size="sm" data-testid={`cs-del-mailbox-${mb.id}`} onClick={() => deleteMailbox(mb.id)}>
                      <Trash2 className="h-4 w-4 text-rose-500" />
                    </Button>
                  </div>
                </div>
                {editBox?.id === mb.id && (
                  <div className="bg-slate-50 rounded-md p-2 space-y-2">
                    <div className="grid grid-cols-2 gap-2">
                      <div><Label className="text-xs">Etichetta</Label>
                        <Input data-testid={`cs-edit-label-input-${mb.id}`} value={editBox.label}
                          onChange={e => setEditBox({ ...editBox, label: e.target.value })} /></div>
                      <div><Label className="text-xs">Email</Label>
                        <Input data-testid={`cs-edit-email-input-${mb.id}`} value={editBox.email}
                          onChange={e => setEditBox({ ...editBox, email: e.target.value })} /></div>
                      <div><Label className="text-xs">Nuova password (vuota = mantieni)</Label>
                        <div className="relative">
                          <Input data-testid={`cs-edit-pw-input-${mb.id}`} type={editBox.show ? 'text' : 'password'}
                            value={editBox.password}
                            onChange={e => setEditBox({ ...editBox, password: e.target.value })} className="pr-9" />
                          <button type="button" data-testid={`cs-edit-pw-toggle-${mb.id}`}
                            onClick={() => setEditBox({ ...editBox, show: !editBox.show })}
                            className="absolute right-2 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600">
                            {editBox.show ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                          </button>
                        </div>
                      </div>
                      <div className="grid grid-cols-2 gap-2">
                        <div><Label className="text-xs">Host IMAP</Label>
                          <Input data-testid={`cs-edit-host-input-${mb.id}`} value={editBox.host}
                            onChange={e => setEditBox({ ...editBox, host: e.target.value })} /></div>
                        <div><Label className="text-xs">Porta</Label>
                          <Input data-testid={`cs-edit-port-input-${mb.id}`} type="number" value={editBox.port}
                            onChange={e => setEditBox({ ...editBox, port: parseInt(e.target.value) || 993 })} /></div>
                      </div>
                    </div>
                    <div className="flex gap-2 justify-end">
                      <Button size="sm" variant="ghost" onClick={() => setEditBox(null)}>Annulla</Button>
                      <Button size="sm" data-testid={`cs-edit-pw-save-${mb.id}`} onClick={saveMailboxPassword} disabled={saving}>Salva</Button>
                    </div>
                  </div>
                )}
              </div>
            ))}
            <div className="border border-dashed border-slate-300 rounded-lg p-3 space-y-2">
              <div className="grid grid-cols-2 gap-2">
                <div><Label className="text-xs">Etichetta</Label>
                  <Input data-testid="cs-new-box-label" value={newBox.label} onChange={e => setNewBox({ ...newBox, label: e.target.value })} placeholder="Customer Service" /></div>
                <div><Label className="text-xs">Email</Label>
                  <Input data-testid="cs-new-box-email" value={newBox.email} onChange={e => setNewBox({ ...newBox, email: e.target.value })} placeholder="customerservice@holidayw.eu" /></div>
                <div><Label className="text-xs">Password IMAP / App-password</Label>
                  <div className="relative">
                    <Input data-testid="cs-new-box-password" type={showNewPw ? 'text' : 'password'} value={newBox.password}
                      onChange={e => setNewBox({ ...newBox, password: e.target.value })} className="pr-9" />
                    <button type="button" data-testid="cs-new-box-password-toggle" onClick={() => setShowNewPw(!showNewPw)}
                      className="absolute right-2 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600">
                      {showNewPw ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                    </button>
                  </div></div>
                <div className="grid grid-cols-2 gap-2">
                  <div><Label className="text-xs">Host</Label>
                    <Input data-testid="cs-new-box-host" value={newBox.host} onChange={e => setNewBox({ ...newBox, host: e.target.value })} /></div>
                  <div><Label className="text-xs">Porta</Label>
                    <Input data-testid="cs-new-box-port" type="number" value={newBox.port} onChange={e => setNewBox({ ...newBox, port: parseInt(e.target.value) || 993 })} /></div>
                </div>
              </div>
              <Button size="sm" data-testid="cs-add-mailbox-btn" onClick={addMailbox} disabled={saving}>
                <Plus className="h-4 w-4 mr-1" /> Aggiungi casella
              </Button>
            </div>
            <div className="border border-slate-200 rounded-lg p-3 space-y-2">
              <Label className="text-xs font-semibold">🚫 Email escluse dalle metriche</Label>
              <p className="text-xs text-slate-500">Una regola per riga. Se il <b>mittente</b> o l'<b>oggetto</b> contiene il testo, l'email viene ignorata (noreply, avvisi di pagamento, notifiche automatiche...). Effetto immediato e retroattivo su tutte le metriche.</p>
              <Textarea data-testid="cs-email-exclusions" rows={6} value={exclusions}
                onChange={e => setExclusions(e.target.value)} placeholder={"noreply\navviso di pagamento"} className="text-sm font-mono" />
              <Button size="sm" data-testid="cs-save-exclusions-btn" onClick={saveExclusions} disabled={saving}>Salva esclusioni</Button>
            </div>
          </TabsContent>

          <TabsContent value="voxloud" className="space-y-3 pt-3">
            <p className="text-xs text-slate-500">Pannello Voxloud → <b>Integrazioni</b> → card <b>API key</b> → copia la chiave. Le chiamate vengono importate automaticamente ogni ora (primo import: ultimi 90 giorni).</p>
            <div className="flex items-center gap-2 text-sm">
              {settings?.voxloud_configured
                ? <span className="inline-flex items-center gap-1 text-emerald-600"><CheckCircle2 className="h-4 w-4" /> Configurato ({settings.voxloud_key_hint})</span>
                : <span className="inline-flex items-center gap-1 text-slate-400"><XCircle className="h-4 w-4" /> Non configurato</span>}
              {settings?.voxloud_last_sync && <span className="text-xs text-slate-400">· ultimo sync {new Date(settings.voxloud_last_sync).toLocaleString('it-IT')}</span>}
            </div>
            <div>
              <Label className="text-xs">Chiave API Voxloud</Label>
              <Input data-testid="cs-voxloud-key-input" type="password" value={voxKey} onChange={e => setVoxKey(e.target.value)}
                placeholder={settings?.voxloud_configured ? 'Lascia vuoto per mantenere quella attuale' : 'Incolla la chiave dal pannello Voxloud'} />
            </div>
            <div className="flex gap-2">
              <Button size="sm" data-testid="cs-voxloud-save-btn" onClick={saveVoxloud} disabled={saving}>Salva chiave</Button>
              <Button size="sm" variant="outline" data-testid="cs-voxloud-test-btn" onClick={testVoxloud} disabled={testing}>
                {testing ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Testa connessione'}
              </Button>
            </div>

            <div className="border-t border-slate-100 pt-3 space-y-2">
              <p className="text-sm font-semibold text-slate-700">📟 Popup chiamate in tempo reale (Caller ID)</p>
              <p className="text-xs text-slate-500">Quando squilla il centralino, tutto l'ufficio vede subito CHI sta chiamando: ospite (con prenotazione), proprietario, partner o responsabile (con le sue case). Registra i webhook direttamente su Voxloud.</p>
              <div className="flex items-center gap-2 text-sm">
                {settings?.voxloud_realtime?.ok
                  ? <span className="inline-flex items-center gap-1 text-emerald-600" data-testid="cs-popup-status-on"><CheckCircle2 className="h-4 w-4" /> Attivo</span>
                  : <span className="inline-flex items-center gap-1 text-slate-400" data-testid="cs-popup-status-off"><XCircle className="h-4 w-4" /> Non attivo</span>}
              </div>
              {settings?.voxloud_realtime?.alert_active && (
                <div className="border border-red-300 bg-red-50 text-red-800 rounded-md p-3 text-sm" data-testid="cs-popup-health-alert">
                  <strong>Collegamento chiamate interrotto.</strong> Il sistema sta tentando il ripristino automatico ogni 2 minuti.
                </div>
              )}
              {!settings?.voxloud_realtime?.alert_active && settings?.voxloud_realtime?.monitor_enabled && (
                <p className="text-xs text-emerald-700" data-testid="cs-popup-monitor-status">
                  Controllo automatico attivo ogni 2 minuti
                  {settings.voxloud_realtime.last_check_at ? ` · ultimo controllo ${new Date(settings.voxloud_realtime.last_check_at).toLocaleString('it-IT')}` : ''}
                </p>
              )}
              <div className="flex gap-2">
                <Button size="sm" data-testid="cs-popup-setup-btn" onClick={setupPopup} disabled={popupBusy || !settings?.voxloud_configured}>
                  {popupBusy ? <Loader2 className="h-4 w-4 animate-spin" /> : (settings?.voxloud_realtime?.ok ? 'Ri-registra webhook' : 'Attiva popup')}
                </Button>
                {settings?.voxloud_realtime?.ok && (
                  <Button size="sm" variant="outline" data-testid="cs-popup-remove-btn" onClick={removePopup} disabled={popupBusy}>Disattiva</Button>
                )}
              </div>
              {!settings?.voxloud_configured && <p className="text-xs text-amber-600">Prima salva la chiave API Voxloud.</p>}
            </div>

            <div className="border-t border-slate-100 pt-3 space-y-2">
              <p className="text-sm font-semibold text-slate-700">🕐 Orari popup e squillo</p>
              <p className="text-xs text-slate-500">
                Fuori da questi orari (es. quando scatta la segreteria) il popup NON appare e lo squillo NON suona in ufficio.
                Le <b>notifiche push sul telefono restano sempre attive</b>. Formato: <span className="font-mono">09:00-13:00, 14:00-18:00</span> — vuoto = chiuso.
              </p>
              {[['weekdays', 'Lun-Ven'], ['saturday', 'Sabato'], ['sunday', 'Domenica']].map(([k, label]) => (
                <div key={k} className="grid grid-cols-[80px_1fr] gap-2 items-center">
                  <Label className="text-xs">{label}</Label>
                  <Input data-testid={`cs-ph-${k}`} value={ph?.[k] || ''} className="font-mono text-sm"
                    onChange={e => setPh({ ...ph, [k]: e.target.value })}
                    placeholder={k === 'weekdays' ? '09:00-13:00, 14:00-18:00' : 'vuoto = chiuso'} />
                </div>
              ))}
              <Button size="sm" data-testid="cs-ph-save-btn" onClick={savePh} disabled={saving}>Salva orari</Button>
            </div>

            <div className="border-t border-slate-100 pt-3 space-y-2">
              <p className="text-sm font-semibold text-slate-700">📇 Rubrica sul centralino Voxloud</p>
              <p className="text-xs text-slate-500">Porta i nomi del PMS sui telefoni fisici: <b>Resp.</b> (responsabili), <b>Partner</b>, <b>Prop.</b> (proprietari) e <b>Ospite</b> (soggiorni da -7 a +30 giorni). Tocca solo i contatti marcati HWsmart, mai quelli inseriti a mano.</p>
              {pbStatus && (
                <p className="text-xs text-slate-500" data-testid="cs-pb-status">
                  {pbStatus.running ? '⏳ Sincronizzazione in corso…' : pbStatus.last_result
                    ? `Ultima: ${pbStatus.last_result.dry_run ? 'anteprima' : 'sync'} — ${pbStatus.last_result.create ?? pbStatus.last_result.created ?? 0} nuovi, ${pbStatus.last_result.update ?? pbStatus.last_result.updated ?? 0} aggiornati, ${pbStatus.last_result.delete ?? pbStatus.last_result.deleted ?? 0} rimossi (rubrica PMS: ${pbStatus.last_result.desired ?? '—'} numeri)`
                    : 'Mai sincronizzata'}
                </p>
              )}
              <div className="flex flex-wrap gap-2 items-center">
                <Button size="sm" variant="outline" data-testid="cs-pb-dry-btn" onClick={pbDry} disabled={pbBusy || pbStatus?.running}>
                  {pbBusy ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Anteprima (senza modifiche)'}
                </Button>
                <Button size="sm" data-testid="cs-pb-sync-btn" onClick={pbRun} disabled={pbBusy || pbStatus?.running}>Sincronizza ora</Button>
                <Button size="sm" variant="ghost" data-testid="cs-pb-refresh-btn" onClick={loadPbStatus}>Aggiorna stato</Button>
                <label className="flex items-center gap-1.5 text-xs text-slate-600 ml-auto">
                  <input type="checkbox" data-testid="cs-pb-enabled" checked={!!pbStatus?.enabled} onChange={e => pbToggle(e.target.checked)} />
                  Sync notturno automatico (4:40, solo produzione)
                </label>
              </div>
            </div>
          </TabsContent>

          <TabsContent value="transcripts" className="space-y-3 pt-3">
            <p className="text-xs text-slate-500">Dopo ogni chiamata <b>in entrata</b> risposta e registrata (≥30s di vera conversazione, filtro anti-attesa/IVR), il sistema invia un'email con <b>riepilogo AI + link alla trascrizione completa</b> (il cliente la sblocca con un voto 1-5 stelle). Controllo automatico ogni 15 minuti (solo in produzione).</p>
            <div className="grid grid-cols-3 gap-2">
              {[['off', '⛔ Spento', 'nessun invio'],
                ['test', '🧪 Test', 'TUTTE le email solo al destinatario di test'],
                ['live', '✅ Operativo', 'email al cliente che ha chiamato']].map(([v, lab, desc]) => (
                <button key={v} type="button" data-testid={`cs-te-mode-${v}`} onClick={() => setTe({ ...te, mode: v })}
                  className={`border rounded-lg p-2 text-left transition-colors ${te?.mode === v ? 'border-indigo-500 bg-indigo-50' : 'border-slate-200 hover:border-slate-300'}`}>
                  <p className="text-sm font-semibold">{lab}</p>
                  <p className="text-[11px] text-slate-500">{desc}</p>
                </button>
              ))}
            </div>
            <div>
              <Label className="text-xs">Destinatari di test / fallback (più email separate da virgola)</Label>
              <Input data-testid="cs-te-recipient" value={te?.test_recipient || ''}
                onChange={e => setTe({ ...te, test_recipient: e.target.value })} placeholder="mas.marchese@holidayw.eu, customerservice@holidayw.eu" />
            </div>
            <label className="flex items-center gap-2 text-xs text-slate-600">
              <input type="checkbox" data-testid="cs-te-fallback" checked={te?.fallback_to_test_recipient !== false}
                onChange={e => setTe({ ...te, fallback_to_test_recipient: e.target.checked })} />
              In Operativo, se l'email del cliente non viene trovata, invia comunque al destinatario di test (da inoltrare a mano)
            </label>
            <div className="border-t border-slate-100 pt-2 space-y-2">
              <label className="flex items-center gap-2 text-xs text-slate-600">
                <input type="checkbox" data-testid="cs-te-wa-enabled" checked={!!te?.whatsapp_enabled}
                  onChange={e => setTe({ ...te, whatsapp_enabled: e.target.checked })} />
                📲 In Operativo, invia il link per il voto via <b>WhatsApp (Spoki) a TUTTI i numeri mobili/esteri</b> — chi ha anche l'email riceve entrambi i canali
              </label>
              {['it', 'en', 'de', 'fr', 'es', 'pt', 'nl', 'pl'].map(lg => (
                <div key={lg} className="grid grid-cols-[28px_1fr_1fr] gap-2 items-center">
                  <span className="text-xs font-bold text-slate-500 uppercase">{lg}</span>
                  <Input data-testid={`cs-te-wa-url-${lg}`} value={te?.whatsapp_webhooks?.[lg]?.webhook_url || ''}
                    onChange={e => setTe({ ...te, whatsapp_webhooks: { ...(te?.whatsapp_webhooks || {}), [lg]: { ...(te?.whatsapp_webhooks?.[lg] || {}), webhook_url: e.target.value } } })}
                    placeholder={`URL webhook automazione Spoki (${lg.toUpperCase()})`} />
                  <Input data-testid={`cs-te-wa-secret-${lg}`} value={te?.whatsapp_webhooks?.[lg]?.secret || ''}
                    onChange={e => setTe({ ...te, whatsapp_webhooks: { ...(te?.whatsapp_webhooks || {}), [lg]: { ...(te?.whatsapp_webhooks?.[lg] || {}), secret: e.target.value } } })}
                    placeholder="Secret (vuoto = usa secret di sistema)" />
                </div>
              ))}
              <div className="flex flex-wrap items-center gap-2 bg-slate-50 border border-slate-200 rounded-lg p-2" data-testid="cs-te-wa-test-row">
                <span className="text-xs text-slate-500">🧪 Prova il collegamento:</span>
                <Input data-testid="cs-te-wa-test-phone" className="w-44 h-8 text-sm" value={waTestPhone}
                  onChange={e => setWaTestPhone(e.target.value)} placeholder="+393401234567" />
                <select data-testid="cs-te-wa-test-lang" value={waTestLang} onChange={e => setWaTestLang(e.target.value)}
                  className="h-8 border border-slate-200 rounded-md text-sm px-2 bg-white">
                  {['it', 'en', 'de', 'fr', 'es', 'pt', 'nl', 'pl'].map(lg => <option key={lg} value={lg}>{lg.toUpperCase()}</option>)}
                </select>
                <Button size="sm" variant="outline" data-testid="cs-te-wa-test-btn" disabled={waTestBusy}
                  onClick={async () => {
                    if (!waTestPhone.trim()) { toast.error('Inserisci il numero per il test'); return; }
                    setWaTestBusy(true);
                    try {
                      const r = await axios.post(`${API}/cs-control/transcript-email/wa-test`,
                        { phone: waTestPhone.trim(), lang: waTestLang }, { headers: authHeaders() });
                      if (r.data.ok) toast.success(`WhatsApp di prova (${waTestLang.toUpperCase()}) inviato a ${r.data.phone} ✅ ${r.data.detail || ''} Controlla il telefono!`, { duration: 8000 });
                      else toast.error(r.data.error || 'Invio di prova fallito', { duration: 10000 });
                    } catch (err) { toast.error(err?.response?.data?.detail || 'Errore invio di prova'); }
                    finally { setWaTestBusy(false); }
                  }}>
                  {waTestBusy ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : `Invia prova ${waTestLang.toUpperCase()}`}
                </Button>
                <span className="text-[10px] text-slate-400">usa i webhook/secret SALVATI — salva prima le modifiche</span>
              </div>
              <p className="text-[11px] text-slate-400" data-testid="cs-te-wa-lang-note">
                🌍 La lingua di email e WhatsApp è riconosciuta <b>automaticamente dal prefisso internazionale</b> del chiamante:
                IT, EN, DE, FR, ES, PT, NL, PL — prefissi non riconosciuti → inglese. Se il webhook della lingua non è configurato si usa EN, poi IT.
              </p>
              <p className="text-[11px] text-slate-400">
                Campi dinamici per il template Spoki: <b>TRANSCRIPT_URL</b> (link completo), <b>TRANSCRIPT_TOKEN</b>, <b>FIRST_NAME</b>.
                Chi riceve il WhatsApp può lasciare la propria email nella pagina: da quel momento il numero viene associato in rubrica
                e le prossime trascrizioni arriveranno direttamente via email.
              </p>
            </div>
            <div className="flex gap-2">
              <Button size="sm" data-testid="cs-te-save-btn" onClick={saveTe} disabled={saving}>Salva</Button>
              <Button size="sm" variant="outline" data-testid="cs-te-run-btn" onClick={runTeNow} disabled={teBusy || te?.mode === 'off'}>
                {teBusy ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Esegui controllo ora'}
              </Button>
              <Button size="sm" variant="ghost" data-testid="cs-te-log-refresh-btn" onClick={loadTeLog}>Aggiorna log</Button>
            </div>
            <div className="border-t border-slate-100 pt-2">
              <p className="text-xs font-semibold text-slate-600 mb-1">📊 Monitor voti dalla landing</p>
              {!teStats ? <p className="text-xs text-slate-400">Aggiorna il log per caricare le statistiche</p> : (
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-2" data-testid="cs-te-stats">
                  <div className="border border-slate-200 rounded-lg p-2 text-center">
                    <p className="text-lg font-bold text-slate-800" data-testid="cs-te-stats-sent">{teStats.sent_live}</p>
                    <p className="text-[10px] text-slate-500">email inviate ai clienti</p>
                  </div>
                  <div className="border border-slate-200 rounded-lg p-2 text-center">
                    <p className="text-lg font-bold text-indigo-700" data-testid="cs-te-stats-votes">
                      {teStats.total_votes}{teStats.conversion_pct != null ? <span className="text-xs font-normal text-slate-400"> ({teStats.conversion_pct}%)</span> : null}
                    </p>
                    <p className="text-[10px] text-slate-500">voti ricevuti dalla landing</p>
                  </div>
                  <div className="border border-slate-200 rounded-lg p-2 text-center bg-blue-50/50">
                    <p className="text-lg font-bold text-blue-800" data-testid="cs-te-stats-full">
                      {teStats.votes.full.count + teStats.votes.legacy.count}
                      {teStats.votes.full.avg != null && <span className="text-xs font-normal text-amber-600"> ★{teStats.votes.full.avg}</span>}
                    </p>
                    <p className="text-[10px] text-slate-500">📄 da "Ricevi la trascrizione"{teStats.votes.legacy.count > 0 ? ` (di cui ${teStats.votes.legacy.count} storici)` : ''}</p>
                  </div>
                  <div className="border border-slate-200 rounded-lg p-2 text-center bg-amber-50/50">
                    <p className="text-lg font-bold text-amber-700" data-testid="cs-te-stats-voteonly">
                      {teStats.votes.vote_only.count}
                      {teStats.votes.vote_only.avg != null && <span className="text-xs font-normal text-amber-600"> ★{teStats.votes.vote_only.avg}</span>}
                    </p>
                    <p className="text-[10px] text-slate-500">⭐ da "Lasci solo un voto"</p>
                  </div>
                  <div className="col-span-2 sm:col-span-4 flex flex-wrap gap-x-4 gap-y-1 text-[11px] text-slate-500 pt-1" data-testid="cs-te-stats-channels">
                    <span>📲 WhatsApp inviati: <b className="text-slate-700">{teStats.wa_sent ?? 0}</b></span>
                    <span>Voti via WhatsApp: <b className="text-emerald-700">{teStats.by_channel?.wa?.count ?? 0}</b>{teStats.by_channel?.wa?.avg != null ? ` (★${teStats.by_channel.wa.avg})` : ''}</span>
                    <span>Voti via Email: <b className="text-blue-700">{teStats.by_channel?.email?.count ?? 0}</b>{teStats.by_channel?.email?.avg != null ? ` (★${teStats.by_channel.email.avg})` : ''}</span>
                  </div>
                </div>
              )}
            </div>
            <div className="border-t border-slate-100 pt-2">
              <p className="text-xs font-semibold text-slate-600 mb-1">Ultimi esiti (max 30)</p>
              {teLog.length === 0 ? <p className="text-xs text-slate-400" data-testid="cs-te-log-empty">Nessuna chiamata processata finora</p> : (
                <div className="space-y-1 max-h-52 overflow-y-auto" data-testid="cs-te-log">
                  {teLog.map((r, i) => (
                    <p key={i} className="text-xs text-slate-500">
                      {r.status === 'sent' ? '✅' : r.status === 'skipped' ? '⏭️' : '❌'}{' '}
                      {(r.sent_at || r.updated_at) ? new Date(r.sent_at || r.updated_at).toLocaleString('it-IT', { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' }) : ''}{' '}
                      · 📞 {r.caller_number || '—'} → {r.to_email || '—'} {r.mode ? `(${r.mode})` : ''}
                      {r.error ? <span className="text-rose-400"> · {r.error}</span> : null}
                    </p>
                  ))}
                </div>
              )}
            </div>
          </TabsContent>

          <TabsContent value="rubrica" className="space-y-3 pt-3">
            <p className="text-xs text-slate-500">
              Trova i numeri di telefono presenti su <b>più schede</b> (responsabili, partner, proprietari, contatti noti, ospiti).
              Le righe <span className="text-red-600 font-semibold">rosse</span> hanno <b>nomi diversi</b> sullo stesso numero: probabili dati di test o errori da correggere.
            </p>
            <div className="flex items-center gap-3">
              <Button size="sm" data-testid="cs-dup-analyze-btn" onClick={loadDuplicates} disabled={dupBusy}>
                {dupBusy ? <Loader2 className="h-4 w-4 animate-spin" /> : '🔍 Analizza rubrica'}
              </Button>
              {dup && (
                <>
                  <span className="text-xs text-slate-600" data-testid="cs-dup-summary">
                    <b>{dup.total}</b> numeri su più schede · <b className="text-red-600">{dup.suspicious}</b> con nomi diversi
                  </span>
                  <label className="flex items-center gap-1.5 text-xs text-slate-600 ml-auto">
                    <input type="checkbox" data-testid="cs-dup-only-susp" checked={dupOnlySusp}
                      onChange={e => setDupOnlySusp(e.target.checked)} />
                    Solo sospetti
                  </label>
                </>
              )}
            </div>
            {dup && (
              <div className="space-y-2 max-h-[26rem] overflow-y-auto" data-testid="cs-dup-list">
                {(dup.rows || []).filter(r => !dupOnlySusp || r.suspicious).length === 0 && (
                  <p className="text-xs text-emerald-600" data-testid="cs-dup-empty">✓ Nessun caso da mostrare con questo filtro</p>
                )}
                {(dup.rows || []).filter(r => !dupOnlySusp || r.suspicious).slice(0, 100).map((r, i) => (
                  <div key={r.norm_phone}
                    className={`border rounded-lg p-2.5 ${r.suspicious ? 'border-red-300 bg-red-50' : 'border-slate-200'}`}
                    data-testid={`cs-dup-row-${i}`}>
                    <div className="flex items-center gap-2">
                      <span className="font-mono font-bold text-sm text-slate-800">📞 {r.numbers.join(' / ')}</span>
                      {r.suspicious && (
                        <span className="text-[10px] font-bold text-red-700 bg-red-100 border border-red-200 rounded-full px-2 py-0.5">
                          ⚠️ {r.distinct_names} NOMI DIVERSI — da verificare
                        </span>
                      )}
                    </div>
                    <div className="mt-1.5 space-y-1">
                      {r.entries.map((e2, j) => (
                        <div key={j} className="flex items-center gap-2 text-xs">
                          <span className={`shrink-0 font-semibold px-1.5 py-0.5 rounded-full ${
                            e2.type === 'responsabile' ? 'bg-sky-100 text-sky-700'
                              : e2.type === 'partner' ? 'bg-amber-100 text-amber-700'
                                : e2.type === 'proprietario' ? 'bg-emerald-100 text-emerald-700'
                                  : e2.type === 'ospite' ? 'bg-indigo-100 text-indigo-700'
                                    : 'bg-slate-100 text-slate-600'}`}>
                            {e2.type}
                          </span>
                          <span className="font-medium text-slate-800">{e2.name}</span>
                          {e2.email && <span className="text-slate-400">{e2.email}</span>}
                          {e2.detail && <span className="text-slate-400">· {e2.detail}</span>}
                          <span className="text-slate-300 ml-auto shrink-0">→ {e2.fix}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </TabsContent>
        </Tabs>
      </DialogContent>
    </Dialog>
  );
}
